import { forwardRef } from 'react';
import { cn } from '../lib/cn';
import { shouldHandleRowClick } from '../lib/rowClick';

/**
 * Tabellen-Primitives (erp-design-guideline.md §7): Zeilenhöhe 36 px,
 * Header neutral-25 + xs + Uppercase, KEINE Zebra-Streifen — 1 px Trennlinien,
 * Hover-Row neutral-25, Zahlen rechtsbündig + tabular-nums.
 */
export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', className)} {...props} />
    </div>
  );
}

export function THead({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn(className)} {...props} />;
}

export function TBody(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props} />;
}

/** forwardRef, damit TR als Radix-`asChild`-Kind (z. B. ContextMenuTrigger) funktioniert (§9). */
export const TR = forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { isClickable?: boolean }
>(function TR({ className, isClickable, onClick, ...props }, ref) {
  /**
   * Portal-Klicks, Links in der Zeile und Modifier-/Mittelklicks sind KEIN
   * Zeilenklick — die Begründung je Fall steht in `shouldHandleRowClick`
   * (dieselbe Prüfung nutzt `DataTable`, statt sie hier zu wiederholen).
   */
  const handleClick = onClick
    ? (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (!shouldHandleRowClick(event)) return;
        onClick(event);
      }
    : undefined;
  return (
    <tr
      ref={ref}
      onClick={handleClick}
      className={cn(
        'border-b border-border',
        isClickable && 'cursor-pointer hover:bg-muted/50',
        className,
      )}
      {...props}
    />
  );
});

export function TH({
  className,
  numeric,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      className={cn(
        'h-9 px-3 text-left align-middle text-xs font-medium text-muted-foreground',
        numeric && 'text-right tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

export function TD({
  className,
  numeric,
  mono,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean; mono?: boolean }) {
  return (
    <td
      className={cn(
        'h-9 px-3 align-middle text-foreground',
        numeric && 'text-right tabular-nums',
        mono && 'font-mono',
        className,
      )}
      {...props}
    />
  );
}
